import { ice } from '@halvard/rules';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useStore } from 'zustand';
import { IceLocalGame } from '../game/ice/IceLocalGame';
import { attackText, outcomeLine } from '../game/ice/matchText';
import { createOnlineMatch, type CreateRequest, type OnlineMatch } from '../game/ice/online';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { SideDot } from '../components/SideDot';
import { useT } from '../i18n';
import { iceServerUrl } from '../net/iceSocket';
import './PlayBot.css';

const TOKEN_KEY = 'halvard.iceGuest';

/** The guest token, kept between visits when the browser allows it. */
const tokens = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Private windows: the guest just won't be remembered.
    }
  },
};

const serverUrl = () =>
  iceServerUrl(window.location, import.meta.env.VITE_ICE_SERVER as string | undefined);

export function inviteLink(origin: string, matchId: string, joinToken: string): string {
  return `${origin}/play/${matchId}?invite=${encodeURIComponent(joinToken)}`;
}

/** `/play/online`: set up a match against a friend, then play it (the URL becomes `/play/:id`). */
export function PlayOnlineSetup() {
  useDocumentTitle(useT().titles.playFriend);
  const [request, setRequest] = useState<CreateRequest | null>(null);
  if (request) return <OnlineMatchView create={request} />;
  return <SetupForm onStart={setRequest} />;
}

/** `/play/:matchId`, with `?invite=…` the first time a friend opens the link. */
export function PlayOnlineMatch() {
  const { matchId } = useParams();
  const [params] = useSearchParams();
  useDocumentTitle(useT().titles.onlineMatch);
  if (!matchId) return null;
  const invite = params.get('invite');
  return (
    <OnlineMatchView key={matchId} matchId={matchId} {...(invite ? { joinToken: invite } : {})} />
  );
}

function SetupForm({ onStart }: { onStart: (request: CreateRequest) => void }) {
  const [mode, setMode] = useState<CreateRequest['mode']>('match');
  const [side, setSide] = useState<CreateRequest['side']>('random');
  const t = useT();
  const o = t.online;
  return (
    <article className="page-page bot-setup">
      <h1>{o.title}</h1>
      <p className="page-lead">{o.intro}</p>
      <fieldset className="bot-picker">
        <legend>{o.format}</legend>
        <div className="bot-strategies">
          <Choice
            pressed={mode === 'match'}
            onClick={() => setMode('match')}
            title={o.matchTitle}
            detail={o.matchDetail}
          />
          <Choice
            pressed={mode === 'single'}
            onClick={() => setMode('single')}
            title={o.singleTitle}
            detail={o.singleDetail}
          />
        </div>
      </fieldset>
      <fieldset className="bot-picker">
        <legend>{mode === 'match' ? o.attackFirst : o.yourSide}</legend>
        <div className="bot-strategies">
          <Choice
            pressed={side === 'random'}
            onClick={() => setSide('random')}
            title={o.random}
            detail={o.randomDetail}
          />
          <Choice
            pressed={side === 'brand'}
            onClick={() => setSide('brand')}
            title={t.sides.brand}
            side="brand"
            detail={o.brandDetail}
          />
          <Choice
            pressed={side === 'rime'}
            onClick={() => setSide('rime')}
            title={t.sides.rime}
            side="rime"
            detail={o.rimeDetail}
          />
        </div>
      </fieldset>
      <p className="bot-start">
        <button
          type="button"
          className="btn btn-primary btn-large"
          onClick={() => onStart({ mode, side, lake: { kind: 'random' } })}
        >
          {o.create}
        </button>
      </p>
    </article>
  );
}

function Choice(props: {
  pressed: boolean;
  onClick: () => void;
  title: string;
  detail: string;
  side?: 'brand' | 'rime';
}) {
  return (
    <button
      type="button"
      className="bot-strategy"
      aria-pressed={props.pressed}
      onClick={props.onClick}
    >
      <strong>
        {props.side && <SideDot side={props.side} />}
        {props.title}
      </strong>
      <span>{props.detail}</span>
    </button>
  );
}

function OnlineMatchView(props: { matchId?: string; joinToken?: string; create?: CreateRequest }) {
  const [match, setMatch] = useState<OnlineMatch | null>(null);
  const { matchId, joinToken, create } = props;

  useEffect(() => {
    const created = createOnlineMatch({
      url: serverUrl(),
      tokens,
      ...(matchId ? { matchId } : {}),
      ...(joinToken ? { joinToken } : {}),
      ...(create ? { create } : {}),
      // The page becomes the match's own address, without reloading it.
      onCreated: (id) => window.history.replaceState(null, '', `/play/${id}`),
    });
    setMatch(created);
    return () => created.close();
  }, [matchId, joinToken, create]);

  if (!match) return null;
  return <MatchScreen match={match} />;
}

function MatchScreen({ match }: { match: OnlineMatch }) {
  const m = useStore(match.meta);
  const g = useStore(match.game);
  const [copied, setCopied] = useState(false);
  const [confirmResign, setConfirmResign] = useState(false);
  const t = useT();
  const o = t.online;
  const seat = m.role === 'host' || m.role === 'guest' ? m.role : null;

  // The invite token is only needed once: drop it from the address bar after joining.
  useEffect(() => {
    if (m.role === 'guest' && m.matchId && window.location.search.includes('invite=')) {
      window.history.replaceState(null, '', `/play/${m.matchId}`);
    }
  }, [m.role, m.matchId]);

  if (m.status === 'outdated') {
    return (
      <article className="page-page">
        <h1>{o.outdatedTitle}</h1>
        <p>{o.outdatedBody}</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          {o.reload}
        </button>
      </article>
    );
  }
  if (!m.role) {
    return (
      <article className="page-page">
        <h1>{m.status === 'offline' ? o.offline : o.connecting}</h1>
        {m.error && <p className="text-danger">{m.error}</p>}
        {m.status === 'offline' && <p>{o.tryingAgain}</p>}
      </article>
    );
  }

  const link =
    m.matchId && m.joinToken ? inviteLink(window.location.origin, m.matchId, m.joinToken) : null;
  const waiting = m.role === 'host' && !m.players.guest;
  const games = m.finished.map((f) => f.result);
  const mySide = seat ? ice.sideOf(m.firstBrand, m.index, seat) : null;
  const opponentSeat = seat ? ice.otherSeat(seat) : null;

  const copy = () => {
    if (!link) return;
    void navigator.clipboard?.writeText(link).then(() => setCopied(true));
  };

  const resultActions = m.upcoming ? (
    <>
      <p className="game-bannerDetail">
        {o.swap(
          games[0] ? attackText(games[0], m.players, seat) : '',
          seat ? ice.sideOf(m.firstBrand, 1, seat) === 'brand' : null,
        )}
      </p>
      <button type="button" className="btn btn-primary" onClick={match.continueToNext}>
        {o.startGame2}
      </button>
    </>
  ) : m.outcome ? (
    <>
      <p className="game-bannerDetail">
        <strong>{outcomeLine(m.outcome, games, m.players, seat).title}.</strong>{' '}
        {outcomeLine(m.outcome, games, m.players, seat).detail}
      </p>
      <Link to="/play/online" className="btn btn-primary">
        {o.newMatch}
      </Link>
    </>
  ) : null;

  const overlay = waiting ? (
    <div className="game-banner" role="status">
      <p className="game-bannerTitle">{o.waitingTitle}</p>
      <p className="game-bannerDetail">{o.waitingDetail}</p>
      {link && (
        <div className="game-row">
          <input
            className="online-link"
            readOnly
            value={link}
            aria-label={o.inviteLink}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="btn btn-primary" onClick={copy}>
            {copied ? o.copied : o.copy}
          </button>
        </div>
      )}
    </div>
  ) : null;

  const panelExtra = (
    <div className="game-section">
      <h2 className="label">{m.mode === 'match' ? o.matchGame(m.index + 1) : o.singleGame}</h2>
      <ul className="ice-stones" aria-label={t.game.players}>
        {(['host', 'guest'] as const).map((s) => (
          <li key={s}>
            <span
              className={`side-dot ${m.connected[s] ? '' : 'ice-dotEmpty'}`}
              aria-hidden="true"
              style={m.connected[s] ? { background: 'var(--success)' } : {}}
            />
            <span>
              {m.players[s]?.name ?? o.waitingName}
              {s === seat ? o.youSuffix : ''}
            </span>
            <span className="game-hint">
              {m.players[s]
                ? m.connected[s]
                  ? t.sides[ice.sideOf(m.firstBrand, m.index, s)]
                  : o.away
                : ''}
            </span>
          </li>
        ))}
      </ul>
      {mySide && !m.outcome && (
        <p className="game-hint">
          {o.youThisGame(mySide === 'brand', t.sides[mySide])}
          {opponentSeat && m.players[opponentSeat] && !m.connected[opponentSeat]
            ? o.opponentAway
            : ''}
        </p>
      )}
      {m.role === 'spectator' && <p className="game-hint">{o.watching}</p>}
      {games.map((game, i) => (
        <p key={i} className="game-hint">
          {o.gameN(i + 1, attackText(game, m.players, seat))}
        </p>
      ))}
      {m.status !== 'online' && <p className="ice-warn">{o.reconnecting}</p>}
      {m.error && <p className="text-danger">{m.error}</p>}
      {seat && !m.outcome && (
        <div className="game-row">
          {confirmResign ? (
            <button
              type="button"
              className="btn btn-small"
              onClick={() => {
                match.resign();
                setConfirmResign(false);
              }}
              onBlur={() => setConfirmResign(false)}
            >
              {o.confirmResign(m.mode === 'match')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-small"
              onClick={() => setConfirmResign(true)}
              disabled={g.game.phase === 'over'}
            >
              {o.resign}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <IceLocalGame
      store={match.game}
      panelExtra={panelExtra}
      resultActions={resultActions}
      overlay={overlay}
      {...(waiting ? { status: o.waitingTitle } : {})}
    />
  );
}
